import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { SettingService } from '../../setting/setting.service';
import { dateAsString, dayAsString, monthAsShortString } from '../../shared/date.helper';
import { FlagStatus, Inspection, UnderwritingStatus } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../../user/user.service';
import { InspectionService } from '../inspection.service';
import * as _ from 'lodash';
import { Filter } from '../../search.filter/search-filter.component';
import { configuredFilters } from './filter-configurations';
import { ProductTypeService } from '../../product.type/product-type.service';
import { AdminAssignmentInspectionComponent } from '../../shared/_components/admin-assignment/admin-assignmentcomponent';
import { InspectionAssigneeFilter } from '../inspection-assignee-filter/inspection-assignee-filter.component';
import { SearchPipe } from '../inspection-flow/search.pipe';
import { InspectionFilterCardsComponent } from '../../shared/_components/inspection-filter-cards/inspection-filter-cards.component';
import { ngxLoadingAnimationTypes } from 'ngx-loading';

const PrimaryWhite = '#ffffff';
const SecondaryGrey = '#ccc';
@Component({
  selector: 'app-flag-flow',
  templateUrl: './flag-flow.component.html',
  styleUrls: ['./flag-flow.component.scss'],
  providers: [SearchPipe],
})
export class FlagFlowComponent implements OnInit, AfterViewInit {
  constructor(
    public inspectionService: InspectionService,
    public userService: UserService,
    private toastr: ToastrService,
    private settingService: SettingService,
    private dialog: DialogService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private productTypeService: ProductTypeService,
    private searchPipe: SearchPipe,
  ) {}

  get orderedAssigned() {
    return [];
  }

  @ViewChild('wrapper', { static: true }) private wrapper: ElementRef;
  @ViewChild('assignmentModal', { static: false }) assignmentModal!: AdminAssignmentInspectionComponent;
  @ViewChild('assignFilter', { static: false }) assignFilter!: InspectionAssigneeFilter;
  @ViewChild('filterCards', { static: false }) filterCards!: InspectionFilterCardsComponent;

  dateAsString = dateAsString;
  public ngxLoadingAnimationTypes = ngxLoadingAnimationTypes;
  public primaryColour = PrimaryWhite;
  public secondaryColour = SecondaryGrey;
  public loadingConfig = {
    backdropBorderRadius: '3px',
    animationType: ngxLoadingAnimationTypes.threeBounce,
    primaryColour: PrimaryWhite,
    secondaryColour: SecondaryGrey,
  };
  definedFilters: Filter[] = configuredFilters;
  activeTab: string = 'open';
  prepare: Inspection[] = [];
  inProgress: Inspection[] = [];
  letterSent: Inspection[] = [];
  resolution: Inspection[] = [];
  closed: Inspection[] = null;
  params = {};
  in_ProgressTable: any[] = null;
  in_ProgressTableCount: number;
  dataLoaded = false;
  customerUrl;
  loadDebounced = new Subject();
  assignedTimeFrame: number;
  needsReviewHighlightTimeFrame: number;
  needsReviewHideTimeframe: number;
  flagGroup: any[] = [];
  allFlags: any[];
  filteredAssignees: number[] = [];
  selectedType;
  canAdd = false;
  canEdit = false;
  canReview = false;
  canRemind = false;
  canExport = false;
  canAssign = false;
  scoreThresholdAmber;
  scoreThresholdOrange;
  scoreThresholdRed;

  sendRemindersToInspectors = false;
  scoresEnabled = true;

  orderBy = 'inspection.completed';
  asc = false;
  page;
  closedCount = 0;
  pageSize;
  search = '';

  productTypes = [];
  filters = {};
  activePrepareFlagsSort = 'highestScored';
  activeInProgressSort = 'highestScored';
  activeLetterSentSort = 'highestScored';
  activeResolutionSort = 'highestScored';
  searchTerm$ = new Subject<string>();
  loading = false;
  currentFilters = {};
  filteredItems: any[] = [];
  selectedProductTypes = [];
  ngAfterViewInit() {
    // wait a bit for dom to render
    setTimeout(() => {
      this.setWrapperHeight();
    }, 500);
  }

  // Set component to use 100% of available height
  setWrapperHeight() {
    this.wrapper.nativeElement.style.height =
      window.innerHeight - this.wrapper.nativeElement.getBoundingClientRect().top + 'px';
  }

  // Re-calculate available height
  @HostListener('window:resize')
  handleResize() {
    this.setWrapperHeight();
  }

  async ngOnInit() {
    this.loading = true;

    // await the parallel requests
    [
      this.scoresEnabled,
      this.scoreThresholdAmber,
      this.scoreThresholdOrange,
      this.scoreThresholdRed,
      this.productTypes,
      this.filters,
    ] = await Promise.all([
      this.settingService.scoresEnabled(),
      this.settingService.scoreThresholdAmber(),
      this.settingService.scoreThresholdOrange(),
      this.settingService.scoreThresholdRed(),
      this.productTypeService.list().then(res => res[0].map(x => ({ ...x, checked: true }))),
      this.userService.getSetting('underwritingFilters'),
    ]);

    this.applyPreference();
    // Debounce data loading
    this.loadDebounced
      .pipe(
        debounceTime(500),
        switchMap(() => of(this.loadDataLive())),
      )
      .subscribe();

    this.loadData();

    this.canAdd = await this.userService.hasPermissions([permissions.INSPECTION.CREATE]);
    this.canEdit = await this.userService.hasPermissions([permissions.INSPECTION.EDIT]);
    this.canReview = await this.userService.hasPermissions([permissions.INSPECTION.REVIEW]);
    this.canRemind = await this.userService.hasPermissions([permissions.INSPECTION.SEND_REMINDERS]);
    this.canExport = await this.userService.hasPermissions([permissions.INSPECTION.EXPORT]);
    this.canAssign = await this.userService.hasPermissions([permissions.INSPECTION.CAN_ASSIGN]);

    this.route.queryParams.subscribe(params => {
      if (params['tab']) {
        this.activeTab = params['tab'] ? params['tab'] : 'open';
      }
      this.asc = params['asc'] ? params['asc'] === 'true' : true;
      this.orderBy = params['orderBy'] ? params['orderBy'] : 'customer.name';
      this.search = params['search'] || '';

      this.pageSize = Number(params['pageSize']) || 10;
      this.page = Number(params['page']) || 1;

      setTimeout(async () => {
        if (params['page']) {
          this.page = Number(params['page'] ? params['page'] : '1');
        }
        if (this.activeTab === 'inprogress') {
          this.loadInprogress();
        } else if (this.activeTab === 'closed') {
          await this.loadClosed();
        } else if (this.activeTab === 'open' && this.dataLoaded) {
          this.loading = false;
        }
        this.params = params;
        this.cdr.detectChanges();
      }, 100);
    });
    this.cdr.detectChanges();

    this.searchTerm$.pipe(debounceTime(500)).subscribe((search: string) => {
      this.search = search;
      this.router.navigate([], {
        queryParams: { search: this.search },
        queryParamsHandling: 'merge',
      });
    });
  }

  setTab(tab: string) {
    this.currentFilters = {};
    this.loading = true;

    this.activeTab = tab;
    if (this.activeTab === 'closed') {
      this.page = 1;
      this.router.navigate([], {
        queryParams: { tab: this.activeTab, page: this.page },
      });
    } else if (this.activeTab === 'inprogress') {
      this.page = 1;
      this.router.navigate([], {
        queryParams: { tab: this.activeTab, page: this.page, orderBy: 'inspection_violationActionRequiredDeadline' },
      });
    } else {
      this.router.navigate([], {
        queryParams: { tab: this.activeTab },
      });
    }
  }

  loadData() {
    this.loadDebounced.next();
  }

  applyPreference() {
    if (this.filters && this.filters['filteredAssignees']) {
      this.filteredAssignees = this.filters['filteredAssignees'];
    }

    this.productTypes = this.productTypes.map(item => {
      if (
        this.filters &&
        this.filters['selectedProductTypes'] &&
        !this.filters['selectedProductTypes'].includes(item.id)
      ) {
        item.checked = false;
      }
      return item;
    });
  }

  async loadClosed() {
    this.loading = true;
    const params = { ...this.getParams() };
    if (this.search) {
      params.search = this.search;
    }
    const allClosed = await this.inspectionService.underwritingClosed(params);
    this.closed = allClosed[0].map(this.mapInspection.bind(this));
    this.closedCount = allClosed[1];
    this.loading = false;
  }

  async loadInprogress() {
    this.loading = true;
    const params = { ...this.getParams() };
    if (this.search) {
      params.search = this.search;
    }
    const in_ProgressTable = await this.inspectionService.underwritingInprogress({ ...params, ...this.currentFilters });
    this.in_ProgressTable = in_ProgressTable[0];
    this.in_ProgressTableCount = in_ProgressTable[1];
    this.loading = false;
  }

  async loadDataLive() {
    const openInspections = await this.inspectionService
      .underwritingOpen()
      .then(x => x.map<any>(this.mapInspection.bind(this)));
    this.dataLoaded = true;
    openInspections.forEach(insp => {
      switch (insp.underwritingStatus) {
        case UnderwritingStatus.LETTER_SENT:
          this.letterSent.push(insp);
          break;

        case UnderwritingStatus.PREPARATION_IN_PROGRESS:
          this.inProgress.push(insp);
          break;

        case UnderwritingStatus.PREPARE_FLAGS:
          this.prepare.push(insp);
          break;

        case UnderwritingStatus.RESOLUTION_IN_PROGRESS:
          this.resolution.push(insp);
          break;
      }
    });

    if (this.scoresEnabled) {
      this.prepare.sort((a, b) => b.flaggingScore - a.flaggingScore);
      this.inProgress.sort((a, b) => b.flaggingScore - a.flaggingScore);
    } else {
      this.inProgress.sort((a: any, b: any) => a.totalDefinedFlags / a.totalFlags - b.totalDefinedFlags / b.totalFlags);
    }

    this.letterSent.sort((a: any, b: any) => {
      if (!a.earliestDeadline) {
        return -1;
      }
      if (!b.earliestDeadline) {
        return 1;
      }

      return new Date(a.earliestDeadline).getTime() - new Date(b.earliestDeadline).getTime();
    });

    this.resolution.sort((a: any, b: any) => {
      if (!a.earliestDeadline) {
        return -1;
      }
      if (!b.earliestDeadline) {
        return 1;
      }

      const dateDiff = new Date(a.earliestDeadline).getTime() - new Date(b.earliestDeadline).getTime();
      if (dateDiff) {
        return dateDiff;
      }

      return a.totalResolvedFlags / a.totalFlags - b.totalResolvedFlags / b.totalFlags;
    });

    const prepareFlagsSortPreference = this.getSortPreference('prepareFlags') || 'highestScored';
    switch (prepareFlagsSortPreference) {
      case 'highestScored':
        this.sortPrepareFlagsHighestSore();
        break;
      case 'oldestAccepted':
        this.sortPrepareFlagsOldestAccepted();
        break;
      case 'newestAccepted':
        this.sortPrepareFlagsNewestAccepted();
        break;
    }

    const inProgressSortPreference = this.getSortPreference('inProgress') || 'highestScored';
    switch (inProgressSortPreference) {
      case 'highestScored':
        this.sortInProgressHighestSore();
        break;
      case 'oldestAccepted':
        this.sortInProgressOldestAccepted();
        break;
      case 'newestAccepted':
        this.sortInProgressNewestAccepted();
        break;
    }

    const letterSentSortPreference = this.getSortPreference('letterSent') || 'highestScored';
    switch (letterSentSortPreference) {
      case 'highestScored':
        this.sortLetterSentHighestSore();
        break;
      case 'oldestAccepted':
        this.sortLetterSentOldestAccepted();
        break;
      case 'newestAccepted':
        this.sortLetterSentNewestAccepted();
        break;
      case 'letterSentEarliestDueDateFirst':
        this.sortLetterSentDueDateFirst('asc', letterSentSortPreference);
        break;
      case 'LetterSentLatestDueDateFirst':
        this.sortLetterSentDueDateFirst('desc', letterSentSortPreference);
        break;
    }

    const resolutionSortPreference = this.getSortPreference('resolution') || 'highestScored';
    switch (resolutionSortPreference) {
      case 'highestScored':
        this.sortResolutionHighestSore();
        break;
      case 'oldestAccepted':
        this.sortResolutionOldestAccepted();
        break;
      case 'newestAccepted':
        this.sortResolutionNewestAccepted();
        break;
      case 'resolutionEarliestDueDateFirst':
        this.sortResolutionDueDateFirst('desc', resolutionSortPreference);
        break;
      case 'resolutionLatestDueDateFirst':
        this.sortResolutionDueDateFirst('asc', resolutionSortPreference);
        break;
    }

    this.activePrepareFlagsSort = this.getSortPreference('prepareFlags') || 'highestScored';
    this.activeInProgressSort = this.getSortPreference('inProgress') || 'highestScored';
    this.activeLetterSentSort = this.getSortPreference('letterSent') || 'highestScored';
    this.activeResolutionSort = this.getSortPreference('resolution') || 'highestScored';
    if (this.activeTab === 'open' || !this.activeTab) {
      this.loading = false;
    }
    try {
      this.activateSortFunction();
    } catch (error) {
      console.log('error', error);
    }
  }

  mapInspection(insp: any) {
    return {
      ...insp,

      earliestDeadlineString: new Date(insp.earliestDeadline).getTime()
        ? monthAsShortString(new Date(insp.earliestDeadline)) + ' ' + dayAsString(new Date(insp.earliestDeadline))
        : '',

      earliestNonResolvedDeadline: new Date(insp.earliestNonResolvedDeadline).getTime()
        ? monthAsShortString(new Date(insp.earliestNonResolvedDeadline)) +
          ' ' +
          dayAsString(new Date(insp.earliestNonResolvedDeadline))
        : '',

      earliestNonResolvedDeadlineIsPast: new Date(insp.earliestNonResolvedDeadline).getTime()
        ? Date.now() > new Date(insp.earliestNonResolvedDeadline).getTime()
        : false,

      deadlineIsPast: new Date(insp.earliestDeadline).getTime()
        ? Date.now() > new Date(insp.earliestDeadline).getTime()
        : false,
      daysSinceAccepted: insp.accepted && this.daysSince(insp.accepted),
    };
  }

  getParams() {
    return {
      pageSize: 10,
      orderBy: this.orderBy ? this.orderBy : 'customer.name',
      page: this.page,
      asc: this.asc,
      search: '',
    };
  }

  sendReminderAssign(ev, inspection: Inspection) {
    ev.stopPropagation();
    this.dialog.confirm().show({
      message: 'Send reminder to vendor?',
      confirmed: async () => {
        await this.inspectionService.sendReminderVendorAssign(inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  sendReminderDue(ev, inspection: Inspection) {
    ev.stopPropagation();

    if (this.sendRemindersToInspectors) {
      this.dialog.confirm().show({
        message: 'Send reminder to inspector?',
        confirmed: async () => {
          await this.inspectionService.sendReminderToInspectorDue(inspection.id);
          this.toastr.success('Message sent!');
        },
      });
    } else {
      this.dialog.confirm().show({
        message: 'Send reminder to vendor?',
        confirmed: async () => {
          await this.inspectionService.sendReminderVendorDue(inspection.id);
          this.toastr.success('Message sent!');
        },
      });
    }
  }

  daysSince(_date) {
    const date = new Date(_date);
    return Math.round((Date.now() - date.getTime()) / 1000 / 60 / 60 / 24);
  }

  downloadCsv(activeTab: string) {
    if (activeTab === 'inprogress') {
      this.inspectionService.downloadUnderwrittingInProgressCsv();
    }
    if (activeTab === 'open') {
      this.inspectionService.downloadUnderwrittingCsv();
    }
  }

  getInspectionScoreClass(inspection) {
    if (!this.scoresEnabled) {
      return {};
    }

    return {
      'score-amber':
        inspection.flaggingScore >= this.scoreThresholdAmber && inspection.flaggingScore < this.scoreThresholdOrange,
      'score-orange':
        inspection.flaggingScore >= this.scoreThresholdOrange && inspection.flaggingScore < this.scoreThresholdRed,
      'score-red': inspection.flaggingScore >= this.scoreThresholdRed,
      'has-score': this.scoresEnabled && inspection.flaggingScore,
    };
  }

  getDaysClass(inspection) {
    return {
      red: inspection.daysSince > this.needsReviewHighlightTimeFrame,
    };
  }

  setOrder(field: string) {
    this.asc = this.orderBy === field ? !this.asc : true;
    this.orderBy = field;
    this.router.navigate([], {
      queryParams: { ...this.currentFilters, tab: this.activeTab, ...this.getParams(), search: this.search },
    });
  }

  getOrderIcon(field: string) {
    return {
      'fa-arrow-up': this.orderBy === field && this.asc,
      'fa-arrow-down': this.orderBy === field && !this.asc,
    };
  }

  onPageChanged(page: number) {
    if (this.loading) {
      return;
    }
    this.router.navigate([], {
      queryParams: { page: page },
      queryParamsHandling: 'merge',
    });
  }

  open(ev, id: number) {
    if (ev.target.tagName === 'TD') {
      this.router.navigateByUrl(`/inspection/review/flagManager/${id}`);
    }
  }

  setSearchTerm(search) {
    this.searchTerm$.next(search);
  }

  saveSortPreference(pref, category) {
    if (!this.filters) {
      this.filters = {};
    }
    this.filters[category] = pref;
    this.savePreference();
  }

  getSortPreference(category) {
    return this.filters?.[category];
  }

  sortPrepareFlagsHighestSore() {
    this.prepare.sort((a: any, b: any) => {
      // First sort
      if (a.flaggingScore && b.flaggingScore) {
        return b.flaggingScore - a.flaggingScore;
      } else if (a.flaggingScore) {
        return -1;
      } else if (b.flaggingScore) {
        return 1;
      }

      // Second sort - the oldest first
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      if (daysSinceA === daysSinceB) {
        return 0;
      }
      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('highestScored', 'prepareFlags');
    this.activePrepareFlagsSort = 'highestScored';
  }

  sortPrepareFlagsOldestAccepted() {
    this.prepare.sort((a: any, b: any) => {
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }

      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('oldestAccepted', 'prepareFlags');
    this.activePrepareFlagsSort = 'oldestAccepted';
  }

  sortPrepareFlagsNewestAccepted() {
    this.prepare.sort((a: any, b: any) => {
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }

      return daysSinceA - daysSinceB;
    });
    this.saveSortPreference('newestAccepted', 'prepareFlags');
    this.activePrepareFlagsSort = 'newestAccepted';
  }

  sortInProgressHighestSore() {
    this.inProgress.sort((a: any, b: any) => {
      if (a.flaggingScore && b.flaggingScore) {
        return b.flaggingScore - a.flaggingScore;
      } else if (a.flaggingScore) {
        return -1;
      } else if (b.flaggingScore) {
        return 1;
      }

      // Second sort - the oldest first
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      if (daysSinceA === daysSinceB) {
        return 0;
      }
      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('highestScored', 'inProgress');
    this.activeInProgressSort = 'highestScored';
  }

  sortInProgressOldestAccepted() {
    this.inProgress.sort((a: any, b: any) => {
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }

      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('oldestAccepted', 'inProgress');
    this.activeInProgressSort = 'oldestAccepted';
  }

  sortInProgressNewestAccepted() {
    this.inProgress.sort((a: any, b: any) => {
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }

      return daysSinceA - daysSinceB;
    });
    this.saveSortPreference('newestAccepted', 'inProgress');
    this.activeInProgressSort = 'newestAccepted';
  }

  sortLetterSentHighestSore() {
    this.letterSent.sort((a: any, b: any) => {
      if (a.flaggingScore && b.flaggingScore) {
        return b.flaggingScore - a.flaggingScore;
      } else if (a.flaggingScore) {
        return -1;
      } else if (b.flaggingScore) {
        return 1;
      }

      // Second sort - the oldest first
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      if (daysSinceA === daysSinceB) {
        return 0;
      }
      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('highestScored', 'letterSent');
    this.activeLetterSentSort = 'highestScored';
  }

  sortLetterSentOldestAccepted() {
    this.letterSent.sort((a: any, b: any) => {
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }

      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('oldestAccepted', 'letterSent');
    this.activeLetterSentSort = 'oldestAccepted';
  }

  sortLetterSentNewestAccepted() {
    this.letterSent.sort((a: any, b: any) => {
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }

      return daysSinceA - daysSinceB;
    });
    this.saveSortPreference('newestAccepted', 'letterSent');
    this.activeLetterSentSort = 'newestAccepted';
  }

  sortLetterSentDueDateFirst(order: 'asc' | 'desc', orderName: string): void {
    this.letterSent = _.orderBy(
      this.letterSent,
      [
        (item: any) => {
          if (item && item.earliestDeadline) {
            return new Date(item.earliestDeadline);
          }
          // For descending order, items without a date should get a very early date to ensure they end up at the end.
          // For ascending order, a very late date ensures they're moved to the end.
          return order === 'asc' ? new Date('9999-12-31') : new Date('0000-01-01');
        },
      ],
      [order],
    );

    this.saveSortPreference(orderName, 'letterSent');
    this.activeLetterSentSort = orderName;
  }

  sortResolutionDueDateFirst(order: 'asc' | 'desc', orderName: string): void {
    this.resolution = _.orderBy(
      this.resolution,
      [
        (item: any) => {
          if (item && item.earliestDeadline) {
            return new Date(item.earliestDeadline);
          }
          // For descending order, items without a date should get a very early date to ensure they end up at the end.
          // For ascending order, a very late date ensures they're moved to the end.
          return order === 'asc' ? new Date('9999-12-31') : new Date('0000-01-01');
        },
      ],
      [order],
    );

    this.saveSortPreference(orderName, 'resolution');
    this.activeResolutionSort = orderName;
  }

  sortResolutionHighestSore() {
    this.resolution.sort((a: any, b: any) => {
      if (a.flaggingScore && b.flaggingScore) {
        return b.flaggingScore - a.flaggingScore;
      } else if (a.flaggingScore) {
        return -1;
      } else if (b.flaggingScore) {
        return 1;
      }

      // Second sort - the oldest first
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      if (daysSinceA === daysSinceB) {
        return 0;
      }
      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('highestScored', 'resolution');
    this.activeResolutionSort = 'highestScored';
  }

  sortResolutionOldestAccepted() {
    this.resolution.sort((a: any, b: any) => {
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }

      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('oldestAccepted', 'resolution');
    this.activeResolutionSort = 'oldestAccepted';
  }

  sortResolutionNewestAccepted() {
    this.resolution.sort((a: any, b: any) => {
      const daysSinceA = this.daysSince(new Date(a.accepted));
      const daysSinceB = this.daysSince(new Date(b.accepted));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }

      return daysSinceA - daysSinceB;
    });
    this.saveSortPreference('newestAccepted', 'resolution');
    this.activeResolutionSort = 'newestAccepted';
  }

  openLetter(hash: string, id: string) {
    const customerUrl = `${location.origin}/recommendation-letter/${encodeURIComponent(hash)}?version=${id}&&skipRegisteringView=true`;
    window.open(customerUrl, 'Recommendation Letter');
  }

  async filtersUpdated(filters: any) {
    this.loading = true;
    this.currentFilters = {};

    for (const filter of filters.filter(f => f.key !== null)) {
      this.currentFilters[filter.key] = filter.value;
      if (filter.operator) {
        this.currentFilters[filter.key + 'Operator'] = filter.operator;
      }
    }

    this.navigate();
    setTimeout(() => (this.loading = false), 400);
  }

  navigate() {
    this.router.navigate([], {
      queryParams: {
        ...this.getParams(),
        ...this.currentFilters,
        tab: this.activeTab,
      },
      relativeTo: this.route,
    });
  }

  totalResolved(flagGroup: any[]): number {
    return flagGroup.reduce((total, fg) => {
      return total + fg.flags.filter(f => f.status === FlagStatus.RESOLVED).length;
    }, 0);
  }

  getLatestSentDate(row: any): string | null {
    if (!row?.recommendationLetter || row.recommendationLetter.length === 0) {
      return null;
    }

    const sentEmailArray = row.recommendationLetter[0]?.sentEmail;
    if (!sentEmailArray || sentEmailArray.length === 0) {
      return null;
    }

    const latestEmail = sentEmailArray.sort(
      (a, b) => new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime(),
    )[0];

    return latestEmail ? latestEmail.sentDate : null;
  }

  filterProductTypes(selected: any[]) {
    if (!selected || selected.length === 0) {
      this.filteredItems = [];
      return;
    }
    this.filters['selectedProductTypes'] = selected.map(item => item.id);
    this.savePreference();
  }

  updateAssignees(assigneeIds: number[]) {
    this.filters['filteredAssignees'] = assigneeIds;
    this.filteredAssignees = assigneeIds;
    this.savePreference();
  }

  getInitials(name: string): string {
    const words = name.trim().split(/\s+/);
    return words.length >= 2 ? words[0][0] + words[1][0] : words[0][0];
  }

  async openAssignmentModal(inspection, selectedType): Promise<void> {
    this.selectedType = selectedType;

    if (this.canAssign) {
      this.assignmentModal.openModal(inspection);
    }
  }

  async handleConfirm(data): Promise<void> {
    const inspectionMapping = {
      letterSent: this.letterSent,
      inProgress: this.inProgress,
      prepare: this.prepare,
      resolution: this.resolution,
    };

    const inspections = inspectionMapping[this.selectedType];

    if (inspections) {
      const inspection = inspections.find(ins => ins.id === data.inspectionId);
      if (inspection) {
        inspection.assignee = data.admin ? data.admin : null;
        if (data.admin) {
          inspection.assigneeId = data?.admin.id;
        } else {
          inspection.assigneeId = null;
        }
      }
    }
    this.cdr.markForCheck();
  }

  calculateResolvedPercentage(totalFlags: number, totalPartiallyResponse: number, totalResolvedFlags: number): number {
    if (totalFlags > 0) {
      return Math.round(((totalResolvedFlags + totalPartiallyResponse) / totalFlags) * 100);
    }
    return 0;
  }

  checkFilteredData(inspections: any[], searchTerm: string, productTypes: any[], filteredAssignees: number[]): boolean {
    return (
      inspections?.length &&
      this.searchPipe.transform(inspections, { searchTerm, productTypes, filteredAssignees }).length > 0
    );
  }

  isletterSent() {
    return !this.checkFilteredData(this.letterSent, this.search, this.productTypes, this.filteredAssignees);
  }

  isPrepare() {
    return !this.checkFilteredData(this.prepare, this.search, this.productTypes, this.filteredAssignees);
  }

  isProgress() {
    return !this.checkFilteredData(this.inProgress, this.search, this.productTypes, this.filteredAssignees);
  }
  isresolution() {
    return !this.checkFilteredData(this.resolution, this.search, this.productTypes, this.filteredAssignees);
  }

  async reset() {
    this.params = {};
    this.search = '';
    this.filteredAssignees = [];
    this.productTypes = await this.productTypeService.list().then(res => res[0].map(x => ({ ...x, checked: true })));
    this.filterCards.clearSearch();
    delete this.filters['selectedProductTypes'];
    this.savePreference();
    this.assignFilter.reset();
    this.loadDataLive();
    this.router.navigate(['/inspection/underwriting'], {
      queryParams: { search: '' },
      queryParamsHandling: 'preserve',
    });
    this.cdr.detectChanges();
  }

  savePreference() {
    this.userService.saveSetting('underwritingFilters', this.filters).subscribe();
  }

  activateSortFunction() {
    const sortFunctions = {
      highestScored: 'HighestSore',
      oldestAccepted: 'OldestAccepted',
      newestAccepted: 'NewestAccepted',
      LetterSentEarliestDueDateFirst: () => this.sortLetterSentDueDateFirst('asc', 'LetterSentEarliestDueDateFirst'),
      LetterSentLatestDueDateFirst: () => this.sortLetterSentDueDateFirst('desc', 'LetterSentLatestDueDateFirst'),
      resolutionEarliestDueDateFirst: () => this.sortResolutionDueDateFirst('asc', 'resolutionEarliestDueDateFirst'),
      resolutionLatestDueDateFirst: () => this.sortResolutionDueDateFirst('desc', 'resolutionLatestDueDateFirst'),
    };

    const applySort = (activeSort: string, prefix: string) => {
      const functionName = sortFunctions[activeSort];
      if (functionName) {
        if (typeof functionName === 'string') {
          this[`${prefix}${functionName}`]();
        } else {
          functionName();
        }
      }
    };

    applySort(this.activeLetterSentSort, 'sortLetterSent');
    applySort(this.activeResolutionSort, 'sortResolution');
    applySort(this.activePrepareFlagsSort, 'sortPrepareFlags');
    applySort(this.activeInProgressSort, 'sortInProgress');
  }
}
